import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { createPortal } from 'react-dom';
import { ReportIssueIcon } from 'components/icons';
import { Box, Button, CircularProgress, Link, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import './report-issue.scss';

const SUPPORT_FORM_URL = 'https://share-eu1.hsforms.com/2aMHmwkzORYOpIHazwsv4kgfggyx';

interface ReportIssueProps {
  showNotification?: (text: string, type?: string, autoclose?: boolean, format?: string) => void;
}

export default function ReportIssue({ showNotification }: ReportIssueProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('technical');
  const [iframeLoading, setIframeLoading] = useState(true);
  const widgetRef = useRef<HTMLDivElement | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!Sentry.isEnabled()) {
      showNotification?.('Sentry is disabled.', 'error', true, 'toast');
      return;
    }

    if (sending) return;
    setSending(true);
    try {
      const trimmedMessage = message.trim();
      const feedbackMessage = trimmedMessage || 'No message provided.';
      const user = Sentry.getCurrentScope?.()?.getUser?.() || {};
      const attachments = [];

      if (screenshotFile) {
        const bytes = new Uint8Array(await screenshotFile.arrayBuffer());
        attachments.push({
          data: bytes,
          filename: screenshotFile.name || 'screenshot.png',
          contentType: screenshotFile.type || 'image/png',
        });
      }

      Sentry.captureFeedback(
        {
          message: feedbackMessage,
          name: user.username || user.name,
          email: user.email,
          url: window.location.href,
          source: 'widget',
        },
        {
          includeReplay: true,
          attachments,
        }
      );

      setMessage('');
      setScreenshotFile(null);
      setOpen(false);
      showNotification?.('Thanks! Your feedback has been sent.', 'success', true, 'toast');
    } catch (error) {
      console.error(error);
      showNotification?.('Failed to send feedback.', 'error', true, 'toast');
    } finally {
      setSending(false);
    }
  }, [message, screenshotFile, sending, showNotification]);

  const preventKeyPropagation = useCallback((event: React.KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  const handleTabChange = useCallback((_: React.SyntheticEvent, nextTab: string) => {
    setActiveTab(nextTab);
    if (nextTab === 'support') setIframeLoading(true);
  }, []);

  const handleScreenshotChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        showNotification?.('Please select an image file.', 'error', true, 'toast');
        event.target.value = '';
        return;
      }
      setScreenshotFile(file);
    },
    [showNotification]
  );

  useEffect(() => {
    if (!open) return undefined;

    // Save the issue's replay buffer before time spent writing feedback replaces it.
    Sentry.getClient()?.emit('openFeedbackWidget');

    const handlePointerDown = (event: MouseEvent) => {
      if (!widgetRef.current) return;
      if (!widgetRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return createPortal(
    <Box className="report-issue-widget" ref={widgetRef}>
      {open && (
        <Paper elevation={4} className="report-issue-panel">
          <Box>
            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" className="report-issue-tabs">
              <Tab value="technical" label="Technical issue / bug" className="report-issue-tab" />
              <Tab value="support" label="Customer Support" className="report-issue-tab" />
            </Tabs>
            {activeTab === 'support' ? (
              <Stack className="report-issue-support-stack">
                <Box className="report-issue-iframe-shell">
                  {iframeLoading && (
                    <Box className="report-issue-iframe-loader">
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <Box
                    component="iframe"
                    src={SUPPORT_FORM_URL}
                    title="Sales and customer support form"
                    onLoad={() => setIframeLoading(false)}
                    className="report-issue-iframe"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  If the form does not load here, open it in a new tab:{' '}
                  <Link href={SUPPORT_FORM_URL} target="_blank" rel="noreferrer">
                    customer support form
                  </Link>
                  .
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Describe the problem you encountered.
                </Typography>
                <TextField
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  onKeyDown={preventKeyPropagation}
                  onKeyUp={preventKeyPropagation}
                  size="small"
                  fullWidth
                  multiline
                  minRows={5}
                  placeholder="What happened? Steps to reproduce? Expected result?"
                />
                <Box className="report-issue-upload-row">
                  <Button variant="outlined" component="label" size="small">
                    {screenshotFile ? 'Replace screenshot' : 'Add screenshot'}
                    <input type="file" accept="image/*" hidden onChange={handleScreenshotChange} />
                  </Button>
                  {screenshotFile && (
                    <>
                      <Typography variant="caption" className="report-issue-upload-name">
                        {screenshotFile.name}
                      </Typography>
                      <Button variant="text" size="small" onClick={() => setScreenshotFile(null)}>
                        Remove
                      </Button>
                    </>
                  )}
                </Box>
                <Box className="report-issue-submit-row">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => void handleSubmit()}
                    disabled={sending || message.trim().length === 0}
                  >
                    {sending ? <CircularProgress size={16} color="inherit" /> : 'Send feedback'}
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        </Paper>
      )}
      <Button className="report-issue-trigger" variant="contained" onClick={() => setOpen((v) => !v)}>
        <ReportIssueIcon />
      </Button>
    </Box>,
    document.body
  );
}
